import React, { useEffect, useState } from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
import { ArrowLeftIcon } from 'lucide-react';
import { degreeLevelOptions, areaStudyOptions, programOptions } from '../data/filterOptions';
interface FormData {
  degreeLevel: number;
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
export function RequestInformationPage() {
  useMetaTags({
    title: 'Find Your Perfect Program | FindOnlineDegrees',
    description: 'Get matched with accredited online degree programs tailored to your goals.'
  });
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    degreeLevel: 0,
    areaOfStudy: 0,
    program: 0,
    highestEducation: '',
    hsGraduationYear: '',
    startDate: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false
  });
  const [error, setError] = useState('');
  useEffect(() => {
    fetchUserLocation();
  }, []);
  const fetchUserLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      updateField('city', data.city || '');
      updateField('state', data.region_code || '');
      updateField('zipCode', data.postal || '');
    } catch (err) {
      console.log('Could not fetch location');
    }
  };
  const lookupZipCode = async (zip: string) => {
    if (zip.length !== 5) return;
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      const data = await response.json();
      if (data.places && data.places[0]) {
        updateField('city', data.places[0]['place name']);
        updateField('state', data.places[0]['state abbreviation']);
      }
    } catch (err) {
      console.log('Could not lookup zip code');
    }
  };
  const updateField = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = () => {
    if (step === 0) {
      if (!formData.degreeLevel) {
        setError('Please select a degree level');
        return;
      }
      if (!formData.areaOfStudy) {
        setError('Please select an area of study');
        return;
      }
      if (!formData.program) {
        setError('Please select a program');
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!formData.highestEducation) {
        setError('Please select your highest level of education');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!formData.hsGraduationYear) {
        setError('Please select when you graduated high school');
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!formData.startDate) {
        setError('Please select when you would like to start classes');
        return;
      }
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address');
        return;
      }
      if (!formData.zipCode.trim()) {
        setError('Please enter your ZIP code');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 5) {
      if (!formData.firstName.trim()) {
        setError('Please enter your first name');
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Please enter your last name');
        return;
      }
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Please enter your phone number');
        return;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
      if (!formData.consent) {
        setError('Please agree to be contacted');
        return;
      }
      handleSubmit();
      return;
    }
  };
  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setError('');
    }
  };
  // Auto-advance when single-select steps get a value
  useEffect(() => {
    if (step === 1 && formData.highestEducation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(2);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, step]);
  useEffect(() => {
    if (step === 2 && formData.hsGraduationYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.hsGraduationYear, step]);
  useEffect(() => {
    if (step === 3 && formData.startDate) {
      const timer = setTimeout(() => {
        setError('');
        setStep(4);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.startDate, step]);
  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Don't allow numbers starting with 1
    if (digits.startsWith('1')) {
      return formData.phone; // Return previous value
    }
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    // Format as XXX-XXX-XXXX
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };
  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: formData.program?.toString(),
        content_category: formData.areaOfStudy?.toString()
      });
    }
    const params = new URLSearchParams({
      mProgram: formData.program.toString(),
      degreeInterest: formData.degreeLevel.toString()
    });
    window.location.href = `/?${params.toString()}`;
  };
  const availablePrograms = formData.areaOfStudy ? programOptions[formData.areaOfStudy] || [] : [];
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1126">
      {/* Landing Page - Step 0 */}
      {step === 0 && <div className="relative" data-id="element-1127">
          {/* Hero Section */}
          <div className="max-w-7xl mx-auto px-4 py-16 md:py-24" data-id="element-1128">
            <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1129">
              {/* Left Column - Content */}
              <div className="text-center lg:text-left" data-id="element-1130">
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1131">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-1132">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1133" />
                  </svg>
                  Trusted by 70,000+ Students
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1134">
                  Find Your Perfect
                  <span className="block text-[#e8594f]" data-id="element-1135">Online Degree</span>
                </h1>

                <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1136">
                  Get matched with accredited programs tailored to your goals.
                  Compare schools, explore financial aid, and start your journey
                  today.
                </p>

                {/* Trust Signals */}
                <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-1137">
                  <div className="text-center" data-id="element-1138">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1139">
                      100%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1140">Online</div>
                  </div>
                  <div className="text-center" data-id="element-1141">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1142">
                      500+
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1143">Programs</div>
                  </div>
                  <div className="text-center" data-id="element-1144">
                    <div className="text-3xl font-bold text-[#e8594f]" data-id="element-1145">
                      Free
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1146">Matching</div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8" data-id="element-1147">
                  {['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No application fees'].map((feature, idx) => <div key={idx} className="flex items-center gap-3 text-gray-700" data-id="element-1148">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-1149">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1150" />
                      </svg>
                      <span className="font-medium" data-id="element-1151">{feature}</span>
                    </div>)}
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 border border-gray-100" data-id="element-1152">
                <div className="text-center mb-8" data-id="element-1153">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-1154">
                    Get Started in 60 Seconds
                  </h2>
                  <p className="text-gray-600" data-id="element-1155">
                    Tell us about your educational goals
                  </p>
                </div>

                <div className="space-y-5" data-id="element-1156">
                  <div data-id="element-1157">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1158">
                      Degree Level <span className="text-red-500" data-id="element-1159">*</span>
                    </label>
                    <select value={formData.degreeLevel} onChange={e => updateField('degreeLevel', Number(e.target.value))} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1160">
                      <option value="" data-id="element-1161">-- Please Select --</option>
                      {degreeLevelOptions.map(option => <option key={option.value} value={option.value} data-id="element-1162">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1163">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1164">
                      Area of Study <span className="text-red-500" data-id="element-1165">*</span>
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', Number(e.target.value));
                  updateField('program', 0);
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1166">
                      <option value="" data-id="element-1167">-- Please Select --</option>
                      {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1168">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1169">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1170">
                      Program <span className="text-red-500" data-id="element-1171">*</span>
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', Number(e.target.value))} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1172">
                      <option value="" data-id="element-1173">
                        {formData.areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                      </option>
                      {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1174">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1175">
                      <p className="text-red-600 text-sm" data-id="element-1176">{error}</p>
                    </div>}

                  <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-1177">
                    Continue to Next Step →
                  </button>

                  <p className="text-center text-xs text-gray-500 mt-4" data-id="element-1178">
                    🔒 Your information is secure and confidential
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Proof Section */}
          <div className="bg-white py-16 border-t border-gray-100" data-id="element-1179">
            <div className="max-w-7xl mx-auto px-4" data-id="element-1180">
              <h3 className="text-center text-2xl font-bold text-gray-900 mb-12" data-id="element-1181">
                Trusted by Students Nationwide
              </h3>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-1182">
                <div className="text-center p-6" data-id="element-1183">
                  <div className="text-5xl mb-4" data-id="element-1184">⭐</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1185">
                    4.8/5
                  </div>
                  <p className="text-gray-600" data-id="element-1186">Average Rating</p>
                </div>
                <div className="text-center p-6" data-id="element-1187">
                  <div className="text-5xl mb-4" data-id="element-1188">🎓</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1189">
                    70K+
                  </div>
                  <p className="text-gray-600" data-id="element-1190">Students Matched</p>
                </div>
                <div className="text-center p-6" data-id="element-1191">
                  <div className="text-5xl mb-4" data-id="element-1192">🏆</div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1193">
                    500+
                  </div>
                  <p className="text-gray-600" data-id="element-1194">Partner Schools</p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Form Steps (1-5) */}
      {step > 0 && <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1195">
          {/* Progress Bar */}
          <div className="mb-12" data-id="element-1196">
            <div className="flex items-center justify-between mb-4 max-w-2xl mx-auto" data-id="element-1197">
              <div className="flex-1 text-center" data-id="element-1198">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step >= 1 && step < 5 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1199">
                  ABOUT YOU
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-1200">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step >= 5 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1201">
                  CONTACT INFO
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-1202">
                <div className={`text-sm font-semibold mb-2 transition-colors ${step > 5 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-1203">
                  YOUR SCHOOL MATCHES
                </div>
              </div>
            </div>

            {/* Progress Bar Track */}
            <div className="relative max-w-2xl mx-auto" data-id="element-1204">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1205">
                <div className="h-full bg-[#e8594f] transition-all duration-500 ease-out" style={{
              width: `${step / 6 * 100}%`
            }} data-id="element-1206" />
              </div>

              {/* Progress Dots */}
              <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-0" data-id="element-1207">
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 1 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1208" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 5 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1209" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step > 5 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-1210" />
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-4" data-id="element-1211">
              Step {step} of 5
            </p>
          </div>

          {/* Back Button */}
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-1212">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1213" />
            <span className="font-medium" data-id="element-1214">Back</span>
          </button>

          {/* Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12" data-id="element-1215">
            {/* Step 1: Highest Education */}
            {step === 1 && <div className="max-w-xl mx-auto" data-id="element-1216">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1217">
                  What is your highest level of education?
                </h2>
                <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1218">
                  <option value="" data-id="element-1219">-- Please Select --</option>
                  <option value="some-high-school" data-id="element-1220">Some High School</option>
                  <option value="high-school" data-id="element-1221">High School Diploma/GED</option>
                  <option value="some-college" data-id="element-1222">Some College</option>
                  <option value="associates" data-id="element-1223">Associate's Degree</option>
                  <option value="bachelors" data-id="element-1224">Bachelor's Degree</option>
                  <option value="masters" data-id="element-1225">Master's Degree</option>
                  <option value="doctorate" data-id="element-1226">Doctorate</option>
                </select>
              </div>}

            {/* Step 2: HS Graduation Year */}
            {step === 2 && <div className="max-w-xl mx-auto" data-id="element-1227">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1228">
                  When did you graduate high school?
                </h2>
                <select value={formData.hsGraduationYear} onChange={e => updateField('hsGraduationYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1229">
                  <option value="" data-id="element-1230">-- Please Select --</option>
                  {years.map(year => <option key={year} value={year} data-id="element-1231">
                      {year}
                    </option>)}
                  <option value="before-1965" data-id="element-1232">Before 1965</option>
                </select>
              </div>}

            {/* Step 3: Start Date */}
            {step === 3 && <div className="max-w-xl mx-auto" data-id="element-1233">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1234">
                  When would you like to start classes?
                </h2>
                <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-1235">
                  <option value="" data-id="element-1236">-- Please Select --</option>
                  <option value="immediately" data-id="element-1237">Immediately</option>
                  <option value="1-3-months" data-id="element-1238">1-3 months</option>
                  <option value="3-6-months" data-id="element-1239">3-6 months</option>
                  <option value="6-12-months" data-id="element-1240">6-12 months</option>
                  <option value="1-year-plus" data-id="element-1241">1+ year</option>
                  <option value="undecided" data-id="element-1242">Undecided</option>
                </select>
              </div>}

            {/* Step 4: Address */}
            {step === 4 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-1243">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-id="element-1244">
                  Where are you located?
                </h2>

                <div data-id="element-1245">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1246">
                    Street Address <span className="text-red-500" data-id="element-1247">*</span>
                  </label>
                  <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 transition-colors" data-id="element-1248" />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3" data-id="element-1249">
                  <span className="text-yellow-600 text-xl" data-id="element-1250">📍</span>
                  <div className="flex-1" data-id="element-1251">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-1252">
                      {formData.city && formData.state && formData.zipCode ? `${formData.city}, ${formData.state} ${formData.zipCode}` : 'Location detected'}
                    </p>
                  </div>
                  <button onClick={() => {
              const zip = prompt('Enter ZIP code to update location:');
              if (zip) {
                updateField('zipCode', zip);
                lookupZipCode(zip);
              }
            }} className="text-sm text-yellow-600 hover:text-yellow-700 font-semibold" data-id="element-1253">
                    edit
                  </button>
                </div>
              </div>}

            {/* Step 5: Contact Info */}
            {step === 5 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-1254">
                <div className="text-center mb-8" data-id="element-1255">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-1256">
                    Last step! Where should we send your matches?
                  </h2>
                  <p className="text-gray-600" data-id="element-1257">
                    We'll match you with top programs in {formData.city},{' '}
                    {formData.state}.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-1258">
                  <div data-id="element-1259">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1260">
                      First Name <span className="text-red-500" data-id="element-1261">*</span>
                    </label>
                    <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} placeholder="John" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1262" />
                  </div>
                  <div data-id="element-1263">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1264">
                      Last Name <span className="text-red-500" data-id="element-1265">*</span>
                    </label>
                    <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} placeholder="Doe" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1266" />
                  </div>
                </div>

                <div data-id="element-1267">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1268">
                    Email Address <span className="text-red-500" data-id="element-1269">*</span>
                  </label>
                  <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="john@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1270" />
                </div>

                <div data-id="element-1271">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1272">
                    Phone Number <span className="text-red-500" data-id="element-1273">*</span>
                  </label>
                  <input type="tel" value={formData.phone} onChange={handlePhoneChange} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-1274" />
                </div>

                <div className="pt-4" data-id="element-1275">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1276">
                    <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f]" data-id="element-1277" />
                    <span className="text-xs text-gray-700 leading-relaxed" data-id="element-1278">
                      *I acknowledge that, by clicking the checkbox as my
                      official signature, I consent to representatives of
                      findonlinedegrees.org and our Partner Schools contacting
                      me about educational opportunities via email, text, or
                      phone, number(s), using an automatic dialer. Message and
                      data rates may apply. I understand that my consent is not
                      a requirement for enrollment, and I may withdraw my
                      consent at any time.
                    </span>
                  </label>
                </div>
              </div>}

            {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg max-w-xl mx-auto" data-id="element-1279">
                <p className="text-red-600 text-sm" data-id="element-1280">{error}</p>
              </div>}

            <div className="mt-8 max-w-xl mx-auto" data-id="element-1281">
              <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-1282">
                {step === 5 ? 'See Matching Colleges 🎓' : 'Continue →'}
              </button>

              {step === 5 && <p className="text-center text-xs text-gray-500 mt-4" data-id="element-1283">
                  By clicking "See Matching Colleges", you agree to our Terms of
                  Service and Privacy Policy.
                </p>}

              <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800" data-id="element-1284">
                <span className="text-blue-500 text-lg leading-none mt-0.5" data-id="element-1285">
                  ℹ️
                </span>
                <span data-id="element-1286">
                  {step === 0 && 'Selecting your degree level and area of study helps us match you with the most relevant accredited programs.'}
                  {step === 1 && 'Your education background helps schools understand where you are in your academic journey and recommend the right program level.'}
                  {step === 2 && 'Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline.'}
                  {step === 3 && 'Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule.'}
                  {step === 4 && 'Your location helps us find programs available in your area and determine eligibility for state-specific financial aid.'}
                  {step === 5 && "Your contact information is only shared with schools you're matched with. We never sell your data to third parties."}
                </span>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}